import React from 'react';
import { useT } from '../i18n';

interface PairData {
  pairId: number;
  textA: string;
  textB: string;
  genre: string | null;
  humanSide: 'A' | 'B';
}

interface RevealData {
  correct: boolean;
  humanSource: string;
  humanYear: number | null;
  aiModel: string;
  humanSide: 'A' | 'B';
}

interface RevealProps {
  reveal: RevealData;
  pair: PairData;
  round: number;
  totalRounds: number;
  onNext: () => void;
}

export default function Reveal({ reveal, pair, round, totalRounds, onNext }: RevealProps) {
  const isLastRound = round >= totalRounds;
  const t = useT();

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 py-8">
      {/* Result */}
      <div className="mb-8 text-center">
        <h2 className="font-display text-2xl md:text-3xl text-stone-800 italic leading-snug">
          {reveal.correct
            ? t('reveal.humanChosen')
            : t('reveal.aiChosen', { model: reveal.aiModel })}
        </h2>
        <div className="w-12 h-px bg-stone-300 mx-auto mt-4 mb-2" />
        <p className="text-stone-400 text-sm font-sans">{t('reveal.explanation')}</p>
      </div>

      {/* Revealed texts */}
      <div className="w-full max-w-4xl grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {(['A', 'B'] as const).map((side) => {
          const isHuman = side === reveal.humanSide;
          return (
            <div
              key={side}
              className={`p-6 md:p-8 rounded-xl border ${
                isHuman ? 'border-emerald-300 bg-emerald-50' : 'border-violet-300 bg-violet-50'
              }`}
            >
              <div className="flex items-center gap-2 mb-4">
                <span
                  className={`text-[11px] font-sans font-semibold px-2.5 py-1 rounded uppercase tracking-wide ${
                    isHuman ? 'bg-emerald-100 text-emerald-700' : 'bg-violet-100 text-violet-700'
                  }`}
                >
                  {isHuman ? t('reveal.human') : t('reveal.ai')}
                </span>
              </div>

              <p className="font-serif text-stone-700 leading-[1.8] whitespace-pre-line text-sm mb-4">
                {side === 'A' ? pair.textA : pair.textB}
              </p>

              <p className="text-xs text-stone-400 font-sans border-t border-stone-200 pt-3">
                {isHuman ? (
                  <>
                    {reveal.humanSource}
                    {reveal.humanYear && ` (${reveal.humanYear})`}
                  </>
                ) : (
                  <>
                    {t('reveal.generatedBy')} {reveal.aiModel}
                  </>
                )}
              </p>
            </div>
          );
        })}
      </div>

      {/* Next button */}
      <button
        onClick={onNext}
        className="px-8 py-3 bg-stone-800 hover:bg-stone-900 text-white font-display font-semibold rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg shadow-stone-300"
      >
        {isLastRound ? t('reveal.viewResults') : t('reveal.next')}
      </button>

      <p className="mt-4 text-xs text-stone-400 font-sans">
        {t('arena.round')} {round} {t('arena.of')} {totalRounds}
      </p>
    </div>
  );
}
